"use client";

import {
  SupportedLanguage,
  supportedLanguages,
} from "../../../config/languages";
import { useClickOutside } from "../../../helpers/hooks/useClickOutside";
import classNames from "classnames/bind";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useRef, useState } from "react";
import styles from "./LanguageDropdown.module.css";

const cx = classNames.bind(styles);

interface LanguageDropdownProps {
  currentLang: SupportedLanguage;
  localizedSlugs?: Record<string, string>;
  variant?: "default" | "dark";
  isScrolled?: boolean;
  isMobileMenuOpen?: boolean;
}

const languages = supportedLanguages.map((lang) => ({
  code: lang.code,
  label: lang.code.toUpperCase(),
  fullLabel: lang.label,
}));

export const LanguageDropdown = ({
  currentLang,
  localizedSlugs,
  variant = "default",
  isScrolled = false,
  isMobileMenuOpen = false,
}: LanguageDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useClickOutside(dropdownRef, () => setIsOpen(false), isOpen);

  const getLanguageHref = (langCode: string) => {
    if (localizedSlugs && localizedSlugs[langCode]) {
      return localizedSlugs[langCode];
    }

    const pathSegments = pathname.split("/");
    pathSegments[1] = langCode;
    return pathSegments.join("/");
  };

  const currentLanguage = languages.find((lang) => lang.code === currentLang);

  return (
    <div
      className={cx("dropdown")}
      ref={dropdownRef}
      data-variant={variant}
      data-mobile-open={isMobileMenuOpen}
    >
      <button
        className={cx("trigger", {
          "trigger-dark": variant === "dark",
          "trigger-scrolled": isScrolled,
          "trigger-mobile-open": isMobileMenuOpen,
        })}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`Change language. Current language: ${currentLanguage?.fullLabel}`}
      >
        <span className={cx("current-lang")}>{currentLanguage?.label}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          className={cx("trigger-arrow", { open: isOpen })}
          aria-hidden="true"
        >
          <path
            d="M10.0005 10.9763L14.1254 6.8515L15.3039 8.03001L10.0005 13.3334L4.69727 8.03001L5.87577 6.8515L10.0005 10.9763Z"
            fill="currentColor"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={cx("menu")} role="menu">
          {languages.map((lang) => (
            <Link
              key={lang.code}
              href={getLanguageHref(lang.code)}
              className={cx("menu-item", {
                active: lang.code === currentLang,
              })}
              onClick={() => setIsOpen(false)}
              role="menuitem"
              aria-current={lang.code === currentLang ? "true" : undefined}
            >
              <span className={cx("menu-item-code")}>{lang.label}</span>
              <span className={cx("menu-item-label")}>{lang.fullLabel}</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};
